// src/stores/walkieStore.js
import { isSelectableUser } from '@/utils/selectorEligibility'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'
import { walkieService } from '@/services/WalkieService'
import { fleetWalkieControl, isFleetPeer } from '@/services/FleetWalkieControl'
import { getInstanceByUid } from '@/utils/aiInstances'
import { trustedPeerName, UNKNOWN_PEER } from '@/utils/walkiePeerName'
import { eventBus } from '@/utils/eventBus'
import { registerRefreshBlocker } from '@/services/backgroundRefresh'
import { playRingtone, playRingback, playConnectTone, playDisconnectTone } from '@/utils/audioTones'

// How long an answered call may stay "Connecting…" waiting for the caller's audio before it is ended with an error.
// A call an AI placed connects in about 3 to 5 s (its mind readies the call), and the fleet accept alone may take up
// to its 15 s callable timeout, so this is generous; a person's call connects in well under a second
// (dh1hFe1IFtRoQmGuMMon).
export const ANSWER_CONNECT_TIMEOUT_MS = 30 * 1000

export const useWalkieStore = defineStore('walkie', () => {
  // ─────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────

  const myUid = ref(null)
  const myName = ref('')
  const myPhotoURL = ref('')
  // The status this person CHOSE in the Walkie tab. Calls never write it: teammates see 'busy' while a call is up
  // and this choice again once it ends (presenceStatus()), so a chosen Do Not Disturb survives a call
  // (docs/bugs/walkie_call_lifecycle_BUG.md).
  const CHOOSABLE_STATUSES = ['available', 'busy', 'dnd']
  const myStatus = ref('available')

  const onlineUsers = ref([]) // Array of { uid, displayName, status, lastSeen }
  const presenceUnsubscribe = ref(null)
  // Teammates whose iPhone takes Walkie calls (userPresence.phoneWalkieUntil, written by the server from their device
  // registration; docs/features/native_comms_suite_FEATURE.md Phase 3).
  const phoneUnsubscribe = ref(null)
  let fleetRoster = new Map()
  let fleetRefreshInterval = null

  // Users collection data (for lastSeen fallback)
  const usersData = ref({}) // { uid: { lastSeen, displayName, ... } }
  const usersUnsubscribe = ref(null)

  // Stale threshold: 5 minutes
  const STALE_THRESHOLD_MS = 5 * 60 * 1000

  // Call state
  const callState = ref('idle') // idle, calling, ringing, connected
  // Accepted and connecting: an incoming call stays 'ringing' from the Accept click until the caller's AUDIO arrives
  // (WalkieService reports an answered call connected on its first audio, not when the connection comes up), which
  // for a call an AI placed is a few seconds (its mind readies the call first). The panels turn Accept into a
  // disabled Connecting… and show End only once connected, and a second Accept is refused: it added the microphone
  // twice and the browser ended the call (docs/bugs/walkie_ai_call_accept_adds_microphone_twice_BUG.md,
  // docs/bugs/walkie_answer_connecting_state_BUG.md). No audio within ANSWER_CONNECT_TIMEOUT_MS ends it with an error.
  const answering = ref(false)
  let answerTimer = null
  // The background cache refresher never reloads this tab during a walkie call
  // (src/services/backgroundRefresh.js).
  registerRefreshBlocker(
    'walkie-call',
    () => callState.value !== 'idle' || walkieService.hasOwnedAudio()
  )
  const currentPeerUid = ref(null)
  const currentPeerName = ref(null)
  const currentCallId = ref(null)
  const callDirection = ref(null) // 'outgoing' | 'incoming'
  const callStartTime = ref(null)
  const isMuted = ref(false)

  // Call history
  const callHistory = ref([]) // Array of past calls
  const historyUnsubscribe = ref(null)

  // The remote voice. App.vue's <audio> element is its ONE sink; the store keeps no element of its own (a second
  // autoplaying sink played the voice twice).
  const remoteStream = ref(null)
  let stopRingtoneFunc = null

  // Heartbeat to keep presence fresh
  let heartbeatInterval = null
  const HEARTBEAT_INTERVAL_MS = 60 * 1000 // 1 minute

  // Cross-tab registry so closing ONE tab doesn't mark the user offline while
  // other walkie-active tabs remain (localStorage + stale-threshold, the
  // activeCallTabCoordinator pattern). Entries are heartbeat-stamped; stale =
  // 3 missed beats, generous because hidden-tab timers are throttled. If
  // localStorage is unavailable the registry reads empty and unload degrades
  // to the legacy write-offline-always behavior.
  const TAB_REGISTRY_KEY = 'gse_walkie_tabs'
  const TAB_STALE_MS = 3 * HEARTBEAT_INTERVAL_MS
  const myTabId = `wt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

  function readTabRegistry() {
    try {
      const raw = localStorage.getItem(TAB_REGISTRY_KEY)
      const reg = raw ? JSON.parse(raw) : {}
      const fresh = {}
      const cutoff = Date.now() - TAB_STALE_MS
      for (const [tabId, stamp] of Object.entries(reg)) {
        if (typeof stamp === 'number' && stamp > cutoff) fresh[tabId] = stamp
      }
      return fresh
    } catch {
      return {}
    }
  }

  function writeTabRegistry(reg) {
    try {
      localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(reg))
    } catch {
      /* quota / private mode — degrade to legacy single-tab behavior */
    }
  }

  function stampTabRegistry() {
    const reg = readTabRegistry()
    reg[myTabId] = Date.now()
    writeTabRegistry(reg)
  }

  function leaveTabRegistry() {
    const reg = readTabRegistry()
    delete reg[myTabId]
    writeTabRegistry(reg)
    return Object.keys(reg).length // live walkie tabs remaining
  }

  // ─────────────────────────────────────────────────────────────
  // Computed
  // ─────────────────────────────────────────────────────────────

  const availableUsers = computed(() => {
    return onlineUsers.value.filter(u => u.uid !== myUid.value && u.status === 'available')
  })

  const busyUsers = computed(() => {
    return onlineUsers.value.filter(u => u.uid !== myUid.value && u.status === 'busy')
  })

  const allOnlineUsers = computed(() => {
    return onlineUsers.value.filter(u => u.uid !== myUid.value && u.status !== 'offline')
  })

  const isInCall = computed(() => {
    return ['calling', 'ringing', 'connected'].includes(callState.value)
  })

  // Signed in as an AI teammate (the agent sign-in, or anyone using an AI's account). Its Walkie phone is its voice
  // worker, and its presence has one writer, its fleet heartbeat. A tab that listened, rang and published as the AI
  // marked it busy on every call, so the CRM refused its worker and the caller rang on
  // (docs/bugs/walkie_ai_account_tab_is_a_second_phone_BUG.md).
  const isFleetAccount = computed(() => isFleetPeer(myUid.value))

  const recentCalls = computed(() => {
    return callHistory.value.slice(0, 20)
  })

  // ─────────────────────────────────────────────────────────────
  // Audio Management (using Web Audio API tones)
  // ─────────────────────────────────────────────────────────────

  function startRingtone() {
    try {
      // Stop any existing ringtone first
      stopRingtone()
      // Start new ringtone and store the stop function
      stopRingtoneFunc = playRingtone()
    } catch (e) {
      console.warn('Ringtone error:', e)
    }
  }

  function stopRingtone() {
    try {
      if (stopRingtoneFunc) {
        stopRingtoneFunc()
        stopRingtoneFunc = null
      }
    } catch (e) {
      // Ignore
    }
  }

  // Caller-side ringback while an OUTBOUND call is dialing (iPAbVDDV). Shares
  // stopRingtoneFunc with the inbound ringtone (one call at a time), so every
  // existing stopRingtone() site — connected / ended / error / reset — silences
  // it with no extra wiring.
  function startRingback() {
    try {
      stopRingtone()
      stopRingtoneFunc = playRingback()
    } catch (e) {
      console.warn('Ringback error:', e)
    }
  }

  function playConnectSound() {
    try {
      playConnectTone()
    } catch (e) {
      console.warn('Connect sound error:', e)
    }
  }

  function playDisconnectSound() {
    try {
      playDisconnectTone()
    } catch (e) {
      console.warn('Disconnect sound error:', e)
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Initialization
  // ─────────────────────────────────────────────────────────────

  async function initialize(uid, displayName, photoURL = '') {
    myUid.value = uid
    myName.value = displayName
    myPhotoURL.value = photoURL

    // An AI's tab is not its phone: no listening, ringing, presence, heartbeat or roster.
    if (isFleetAccount.value) {
      console.log(
        '[WalkieStore] AI account: its voice worker answers its calls, so this tab does not:',
        uid
      )
      return
    }

    // Set up callbacks BEFORE listening: a signal already waiting in the queue is handled on the first snapshot.
    walkieService.onIncomingCall = handleIncomingCall
    walkieService.onCallConnected = handleCallConnected
    walkieService.onCallEnded = handleCallEnded
    walkieService.onRemoteStream = handleRemoteStream
    walkieService.onError = handleError

    // Set up walkie service
    walkieService.setMyName(displayName)
    walkieService.startListening(uid)

    // Register this tab, then publish my presence with the status I chose last time (a reload or a new sign-in
    // keeps Do Not Disturb until I change it).
    stampTabRegistry()
    myStatus.value = await readChosenStatus(uid)
    await publishPresence()

    // Listen to other users' presence
    startPresenceListener()

    // Listen to call history
    startHistoryListener()

    // Set up visibility change handler
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Set up beforeunload to mark offline
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Start heartbeat to keep presence fresh
    startHeartbeat()

    console.log('[WalkieStore] Initialized for:', uid)
  }

  function startHeartbeat() {
    // Clear any existing heartbeat
    if (heartbeatInterval) clearInterval(heartbeatInterval)

    // Update presence every minute to keep lastSeen fresh
    heartbeatInterval = setInterval(() => {
      stampTabRegistry()
      if (myUid.value) publishPresence()
    }, HEARTBEAT_INTERVAL_MS)
  }

  function stopHeartbeat() {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }
  }

  /**
   * Sign-out teardown (the chat module's cleanup, then App.vue's logout watcher; both run on every sign-out). Returns
   * the store to its state before initialize(): App.vue re-initialises only when `myUid` is empty, so an identity
   * left behind would keep walkie dead for the next sign-in, and a second run would write presence after auth is
   * gone. The second run finds nothing to do.
   */
  function cleanup() {
    if (!myUid.value) return
    // A call still up at sign-out ends here, logged while this person is still known.
    finishCall({ reason: 'local_hangup', publish: false })
    walkieService.stopListening()
    stopPresenceListener()
    stopHistoryListener()
    stopHeartbeat()
    leaveTabRegistry()
    // The user is genuinely going offline, so this write does not wait for their other tabs (an AI account's tab
    // writes nothing: its heartbeat owns its presence).
    publishPresence('offline')
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('beforeunload', handleBeforeUnload)

    myUid.value = null
    myName.value = ''
    myPhotoURL.value = ''
    myStatus.value = 'available'
    onlineUsers.value = []
    usersData.value = {}
    mergePresenceData._lastPresence = null
    callHistory.value = []
  }

  function handleVisibilityChange() {
    if (!document.hidden) {
      // Tab is visible again
      stampTabRegistry()
      publishPresence()
    }
  }

  function handleBeforeUnload() {
    // Mark offline only when the LAST walkie-active tab closes — closing one
    // of several tabs must not flap the user offline for teammates (the
    // survivors' heartbeats would only repair it a minute later).
    const remainingTabs = leaveTabRegistry()
    if (remainingTabs === 0) {
      publishPresence('offline')
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Push Notifications
  // ─────────────────────────────────────────────────────────────

  async function requestNotificationPermission() {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications')
      return false
    }

    if (Notification.permission === 'granted') {
      return true
    }

    // Only request if not already denied - will be triggered by user action
    if (Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission()
        return permission === 'granted'
      } catch (e) {
        console.warn('Could not request notification permission:', e)
        return false
      }
    }

    return false
  }

  // Call this when user initiates their first call
  async function ensureNotificationPermission() {
    // iOS Safari has no Web Notifications API — skip (voice chat still works).
    if (typeof Notification === 'undefined') return
    if (Notification.permission === 'default') {
      await requestNotificationPermission()
    }
  }

  function showIncomingCallNotification(fromName) {
    if (typeof Notification === 'undefined') return
    if (Notification.permission !== 'granted') return
    if (!document.hidden) return // Only show if tab is not visible

    // Never `requireInteraction` on a call notification: it locked up the whole Chrome UI on macOS
    // (docs/playbooks/call-system.md Trap 1). The ringtone and the auto-close below cover the unattended case.
    const notification = new Notification('Incoming Call', {
      body: `${fromName} is trying to reach you`,
      icon: '/img/icons/walkie-icon.png', // Add this icon to your public folder
      tag: 'walkie-incoming-call',
      vibrate: [200, 100, 200],
    })

    notification.onclick = () => {
      window.focus()
      notification.close()
    }

    // Auto-close after 30 seconds
    setTimeout(() => notification.close(), 30000)

    return notification
  }

  // ─────────────────────────────────────────────────────────────
  // Presence Management
  // ─────────────────────────────────────────────────────────────

  // What teammates see: busy while a call is up, otherwise the status this person chose.
  function presenceStatus() {
    return isInCall.value ? 'busy' : myStatus.value
  }

  /**
   * Publish my presence: `presenceStatus()` unless a status is given ('offline' at sign-out / last tab closed).
   * `chosenStatus` carries the Walkie tab choice, so the next initialize() restores it.
   */
  async function publishPresence(status = presenceStatus()) {
    if (!myUid.value || isFleetAccount.value) return

    const presenceRef = doc(db, 'userPresence', myUid.value)
    try {
      await setDoc(
        presenceRef,
        {
          uid: myUid.value,
          displayName: myName.value,
          photoURL: myPhotoURL.value,
          status,
          chosenStatus: myStatus.value,
          lastSeen: serverTimestamp(),
          onCallWith: currentPeerUid.value || null,
        },
        { merge: true }
      )
    } catch (e) {
      console.warn('[WalkieStore] Failed to publish presence:', e)
    }
  }

  /** The Walkie tab's status picker: available, busy or Do Not Disturb, kept until this person changes it. */
  async function setMyStatus(status) {
    if (!CHOOSABLE_STATUSES.includes(status)) return
    myStatus.value = status
    await publishPresence()
  }

  // The status chosen in an earlier session (presence doc `chosenStatus`); 'available' when there is none.
  async function readChosenStatus(uid) {
    try {
      const snap = await getDoc(doc(db, 'userPresence', uid))
      const chosen = snap.exists() ? snap.data()?.chosenStatus : null
      return CHOOSABLE_STATUSES.includes(chosen) ? chosen : 'available'
    } catch {
      return 'available'
    }
  }

  function startPresenceListener() {
    async function refreshFleet() {
      const uid = myUid.value
      try {
        const result = await fleetWalkieControl('roster')
        if (uid !== myUid.value) return
        fleetRoster = new Map((result?.agents || []).map(agent => [agent.uid, agent]))
      } catch {
        if (uid !== myUid.value) return
        fleetRoster = new Map()
      }
      mergePresenceData(null)
    }
    refreshFleet()
    fleetRefreshInterval = setInterval(refreshFleet, 15000)
    // Listen to userPresence collection
    const presenceRef = collection(db, 'userPresence')
    const q = query(presenceRef, where('status', 'in', ['available', 'busy', 'dnd']))

    presenceUnsubscribe.value = onSnapshot(q, snapshot => {
      const presenceData = {}
      snapshot.docs.forEach(d => {
        presenceData[d.id] = { uid: d.id, ...d.data() }
      })
      mergePresenceData(presenceData)
    })

    // Callable with no browser open: their iPhone rings through CallKit (a VoIP push on the offer).
    phoneUnsubscribe.value = onSnapshot(
      query(presenceRef, where('phoneWalkieUntil', '>', new Date())),
      snapshot => {
        const phones = {}
        snapshot.docs.forEach(d => {
          phones[d.id] = { uid: d.id, ...d.data() }
        })
        mergePresenceData._phones = phones
        mergePresenceData(null)
      },
      error =>
        console.warn('[WalkieStore] Could not read who takes Walkie calls on their phone:', error)
    )

    // Fetch users once for displayName/photoURL fallback (names rarely change).
    // Refresh every 10 minutes instead of a real-time listener on all user docs.
    async function fetchUsersOnce() {
      try {
        let snapshot
        try {
          snapshot = await getDocs(collection(db, 'users'))
        } catch {
          // A teammate who cannot read user records gets the server-written directory (functions/users/syncPublicUsers.js),
          // so every peer is named from the CRM's records (directoryName), never from their own tab.
          snapshot = await getDocs(collection(db, 'publicUsers'))
        }
        const data = {}
        snapshot.docs.forEach(d => {
          data[d.id] = { uid: d.id, ...d.data() }
        })
        usersData.value = data
        mergePresenceData(null)
      } catch (e) {
        console.warn('[WalkieStore] Failed to fetch users:', e)
      }
    }
    fetchUsersOnce()
    const usersRefreshInterval = setInterval(fetchUsersOnce, 10 * 60 * 1000)
    usersUnsubscribe.value = () => clearInterval(usersRefreshInterval)
  }

  function mergePresenceData(presenceData) {
    // If presenceData is provided, store it for future merges
    if (presenceData) {
      mergePresenceData._lastPresence = presenceData
    }
    const currentPresence = mergePresenceData._lastPresence || {}

    const now = Date.now()
    const merged = []
    const addedUids = new Set()

    // First, add all users with active presence
    Object.values(currentPresence).forEach(p => {
      const userData = usersData.value[p.uid] || {}
      if (!isSelectableUser(userData)) return
      const presenceLastSeen = p.lastSeen?.toDate?.() || p.lastSeen
      const userLastSeen = userData.lastSeen?.toDate?.() || userData.lastSeen

      // Use the most recent lastSeen
      const mostRecentLastSeen =
        presenceLastSeen && userLastSeen
          ? new Date(presenceLastSeen) > new Date(userLastSeen)
            ? presenceLastSeen
            : userLastSeen
          : presenceLastSeen || userLastSeen

      // Check if stale (presence says online but lastSeen is old)
      const isStale =
        mostRecentLastSeen && now - new Date(mostRecentLastSeen).getTime() > STALE_THRESHOLD_MS

      if (!isStale) {
        merged.push({
          uid: p.uid,
          // The CRM's record first: a presence record's name is written by that teammate's own tab.
          displayName: directoryName(p.uid) || p.displayName || userData.name || 'Unknown',
          photoURL: p.photoURL || userData.photoURL || '',
          status: p.status,
          lastSeen: mostRecentLastSeen,
          onCallWith: p.onCallWith,
        })
        addedUids.add(p.uid)
      }
    })

    // Then, teammates with no live browser whose iPhone takes Walkie calls (not on Do Not Disturb): callable, "On phone".
    Object.values(mergePresenceData._phones || {}).forEach(p => {
      if (addedUids.has(p.uid) || p.uid === myUid.value) return
      const until = p.phoneWalkieUntil?.toDate?.() || p.phoneWalkieUntil
      if (!until || new Date(until).getTime() <= now || p.chosenStatus === 'dnd') return
      const userData = usersData.value[p.uid] || {}
      if (!isSelectableUser(userData)) return
      merged.push({
        uid: p.uid,
        displayName: directoryName(p.uid) || userData.name || 'Unknown',
        photoURL: userData.photoURL || '',
        status: 'available',
        onPhone: true,
        lastSeen: null,
        onCallWith: null,
      })
      addedUids.add(p.uid)
    })

    // Then, add users from users collection who are recently active but have no presence doc
    Object.values(usersData.value).forEach(u => {
      if (!isSelectableUser(u)) return
      // Skip if already added from presence
      if (addedUids.has(u.uid)) return
      // Skip if it's me
      if (u.uid === myUid.value) return

      const userLastSeen = u.lastSeen?.toDate?.() || u.lastSeen
      if (!userLastSeen) return

      const timeSinceLastSeen = now - new Date(userLastSeen).getTime()

      // If seen within threshold, show as "available" (they're active in the app)
      if (timeSinceLastSeen < STALE_THRESHOLD_MS) {
        merged.push({
          uid: u.uid,
          displayName: directoryName(u.uid) || u.name || u.email || 'Unknown',
          photoURL: u.photoURL || '',
          status: 'available', // Default to available since they're recently active
          lastSeen: userLastSeen,
          onCallWith: null,
        })
        addedUids.add(u.uid)
      }
    })

    onlineUsers.value = merged.map(user => {
      if (!isFleetPeer(user.uid)) return user
      const voice = fleetRoster.get(user.uid)
      return {
        ...user,
        displayName: voice?.displayName || user.displayName,
        status:
          !voice || voice.status === 'offline'
            ? 'offline'
            : voice.status === 'busy'
              ? 'busy'
              : user.status,
      }
    })
  }

  function stopPresenceListener() {
    if (fleetRefreshInterval) clearInterval(fleetRefreshInterval)
    fleetRefreshInterval = null
    fleetRoster = new Map()
    if (presenceUnsubscribe.value) {
      presenceUnsubscribe.value()
      presenceUnsubscribe.value = null
    }
    if (phoneUnsubscribe.value) {
      phoneUnsubscribe.value()
      phoneUnsubscribe.value = null
    }
    mergePresenceData._phones = {}
    if (usersUnsubscribe.value) {
      usersUnsubscribe.value()
      usersUnsubscribe.value = null
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Call History
  // ─────────────────────────────────────────────────────────────

  function startHistoryListener() {
    if (!myUid.value) return

    const historyRef = collection(db, 'walkieHistory')
    const q = query(
      historyRef,
      where('participants', 'array-contains', myUid.value),
      orderBy('startedAt', 'desc'),
      limit(50)
    )

    historyUnsubscribe.value = onSnapshot(q, snapshot => {
      callHistory.value = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
      }))
    })
  }

  function stopHistoryListener() {
    if (historyUnsubscribe.value) {
      historyUnsubscribe.value()
      historyUnsubscribe.value = null
    }
  }

  /**
   * ONE record per call per person: this person's own view of the call, at walkieHistory/{callId}_{uid} with
   * participants [uid]. The peer's device logs theirs, so each person's history shows a call once; another tab of
   * this person that ends the same call (both ring, the caller gives up) rewrites the same record. Records written
   * before 2026-09-25 carry both participants (docs/bugs/walkie_call_lifecycle_BUG.md; firestore.rules).
   */
  async function logCall(call, status) {
    const me = myUid.value
    if (!me || !call.callId || !call.peerUid) return
    const outgoing = call.direction === 'outgoing'
    try {
      await setDoc(doc(db, 'walkieHistory', `${call.callId}_${me}`), {
        callId: call.callId,
        participants: [me],
        callerUid: outgoing ? me : call.peerUid,
        callerName: outgoing ? myName.value : call.peerName,
        receiverUid: outgoing ? call.peerUid : me,
        receiverName: outgoing ? call.peerName : myName.value,
        status, // 'completed', 'missed', 'declined'
        duration: call.duration, // in seconds
        startedAt: serverTimestamp(),
      })
    } catch (e) {
      console.warn('[WalkieStore] Failed to log the call:', e)
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Call Actions
  // ─────────────────────────────────────────────────────────────

  let teamVoiceAdapter = null
  function setTeamVoiceAdapter(adapter) {
    teamVoiceAdapter = typeof adapter === 'function' ? adapter : null
  }
  function setAudioOwnership(ownership) {
    walkieService.setAudioOwnership(ownership)
  }

  async function startCall(peerUid, peerName) {
    if (isFleetAccount.value) {
      console.warn(
        '[WalkieStore] An AI account places its Walkie calls through its voice worker, not a browser'
      )
      return false
    }
    if (isInCall.value) {
      console.warn('[WalkieStore] Already in a call')
      return false
    }

    if (teamVoiceAdapter && !isFleetPeer(peerUid)) {
      await teamVoiceAdapter(peerUid, peerName)
      // Preflight is visible; no call or microphone has started yet.
      return false
    }

    // Request notification permission on first call (user gesture)
    await ensureNotificationPermission()

    callState.value = 'calling'
    callDirection.value = 'outgoing'
    currentPeerUid.value = peerUid
    currentPeerName.value = directoryName(peerUid) || peerName

    // The service names the call before its first await, so the id is known for the whole dial (an answer or a
    // busy decline can then only end THIS call).
    const dialing = walkieService.call(peerUid, peerName)
    currentCallId.value = walkieService.currentCallId
    const success = await dialing

    // Hung up while it was dialing (the microphone prompt, the offer in flight): finishCall already ended it.
    if (callState.value !== 'calling' || currentPeerUid.value !== peerUid) return false
    if (!success) {
      resetCallState()
      return false
    }

    // Audible cue that the call is actually ringing on the other end —
    // stopped by handleCallConnected / finishCall / resetCallState (all route
    // through stopRingtone).
    startRingback()

    await publishPresence()
    return true
  }

  async function acceptCall() {
    if (callState.value !== 'ringing' || answering.value) {
      console.warn('[WalkieStore] No incoming call to accept')
      return false
    }

    answering.value = true
    stopRingtone()
    const callId = currentCallId.value
    clearAnswerTimer()
    answerTimer = setTimeout(() => answerTimedOut(callId), ANSWER_CONNECT_TIMEOUT_MS)

    const success = await walkieService.acceptCall(callId, currentPeerUid.value)

    if (!success) {
      resetCallState()
      return false
    }

    await publishPresence()
    return true
  }

  // An answer whose audio never arrived: end the call through the one end, tell the caller, and say why, instead of
  // leaving the panel on Connecting… (dh1hFe1IFtRoQmGuMMon). A connected or already-ended call is not this one.
  function answerTimedOut(callId) {
    answerTimer = null
    if (!answering.value || currentCallId.value !== callId) return
    eventBus.emit('toast', {
      type: 'error',
      message: 'The walkie call could not connect: no audio arrived. Please try again.',
    })
    finishCall({ reason: 'error' })
    walkieService.hangup()
  }

  function clearAnswerTimer() {
    if (answerTimer) clearTimeout(answerTimer)
    answerTimer = null
  }

  async function declineCall() {
    if (callState.value !== 'ringing') return
    const callId = currentCallId.value
    const peerUid = currentPeerUid.value
    finishCall({ outcome: 'declined', tone: false })
    await walkieService.declineCall(callId, peerUid)
  }

  async function hangup() {
    finishCall({ reason: 'local_hangup' })
    // Tells the peer and this person's other tabs, and releases the media. Its end report finds the call finished.
    await walkieService.hangup()
  }

  function toggleMute() {
    isMuted.value = !isMuted.value
    walkieService.setMuted(isMuted.value)
  }

  // ─────────────────────────────────────────────────────────────
  // Callbacks from WalkieService
  // ─────────────────────────────────────────────────────────────

  // A peer's name from the CRM's own records, keyed by uid (docs/bugs/walkie_caller_name_trust_BUG.md): the AI list, then
  // the user directory this tab holds. Never the name an offer or a presence record carries.
  function directoryName(uid) {
    return trustedPeerName(uid, { aiInstance: getInstanceByUid(uid), record: usersData.value[uid] })
  }

  /** directoryName, reading the server-written directory when this tab does not hold the peer yet. */
  async function lookupPeerName(uid) {
    const known = directoryName(uid)
    if (known || !uid) return known
    try {
      const snap = await getDoc(doc(db, 'publicUsers', uid))
      return snap.exists() ? trustedPeerName(uid, { record: snap.data() }) : null
    } catch (e) {
      console.warn('[WalkieStore] Could not look up the caller:', e)
      return null
    }
  }

  // The caller's own app wrote the offer's name (_claimedName), so it is never shown: the caller is named from the CRM's
  // record of the uid firestore.rules binds to the signed-in sender.
  function handleIncomingCall(fromUid, _claimedName, callId) {
    const known = directoryName(fromUid)
    console.log('[WalkieStore] Incoming call from:', known || fromUid)

    callState.value = 'ringing'
    callDirection.value = 'incoming'
    currentPeerUid.value = fromUid
    currentPeerName.value = known || UNKNOWN_PEER
    currentCallId.value = callId

    // Play ringtone
    startRingtone()

    // Show push notification if tab is in background
    if (known) {
      showIncomingCallNotification(known)
      return
    }
    // Not in this tab's directory yet: read it, then name the caller (only while this call still rings).
    lookupPeerName(fromUid).then(name => {
      if (currentCallId.value !== callId) return
      if (name) currentPeerName.value = name
      if (callState.value === 'ringing') showIncomingCallNotification(currentPeerName.value)
    })
  }

  // A call this tab placed: its connection is up. A call it answered: the caller's audio has arrived.
  function handleCallConnected() {
    // A late report for a call that already ended (the person cancelled while it connected) is not a call.
    if (callState.value === 'idle') return
    console.log('[WalkieStore] Call connected')

    clearAnswerTimer()
    stopRingtone()
    playConnectSound()

    callState.value = 'connected'
    answering.value = false
    callStartTime.value = Date.now()
  }

  // Reported by WalkieService: the peer hung up or declined, the connection dropped, this tab hung up, or another
  // tab of this person answered or declined. Our own hangup()/declineCall() already finished the call, so their
  // report finds nothing to do.
  function handleCallEnded(reason, callId = null) {
    console.log('[WalkieStore] Call ended:', reason)
    // Only the call this store is on: a late report for a call that already ended (or another call) is not this one.
    if (callId && currentCallId.value && callId !== currentCallId.value) return
    if (reason === 'answered_elsewhere') {
      // The call lives on in another tab: no record, no tone, and no presence write over that tab's 'busy'.
      finishCall({ reason, tone: false, publish: false })
      return
    }
    finishCall({ reason })
  }

  function handleRemoteStream(stream) {
    console.log('[WalkieStore] Remote stream received')
    // App.vue's <audio> element plays it.
    remoteStream.value = stream
  }

  function handleError(error) {
    console.error('[WalkieStore] Error:', error)
    eventBus.emit('toast', {
      type: 'error',
      message: error?.message || 'The walkie call could not connect.',
    })
    finishCall({ reason: 'error' })
  }

  // ─────────────────────────────────────────────────────────────
  // The one end of a call
  // ─────────────────────────────────────────────────────────────

  // What this person's record says. An answered call is completed whoever ends it; an unanswered one someone gave
  // up on (either side hung up, or the peer was busy) is missed; a decline is declined. A call whose connection
  // failed before it was answered leaves no record, as before.
  function outcomeFor(reason, call) {
    if (call.state === 'connected') return 'completed'
    if (reason === 'declined') return 'declined'
    if (reason === 'local_hangup' || reason === 'remote_hangup' || reason === 'busy')
      return 'missed'
    return null
  }

  /**
   * Every way a call ends lands here, and only the FIRST for a call does anything (docs/bugs/walkie_call_lifecycle_BUG.md).
   * It takes the call's facts and returns the store to idle BEFORE anything is awaited, so an end event that arrives
   * a moment later (the service reporting our own hangup, the connection dropping as the peer leaves) finds no call.
   * Then, once: this person's record, the disconnect tone, and their chosen status published again.
   *
   * @returns {boolean} true when this call finished it; false when there was no call.
   */
  function finishCall({ reason = null, outcome = null, tone = true, publish = true } = {}) {
    if (callState.value === 'idle') return false
    const call = {
      callId: currentCallId.value,
      peerUid: currentPeerUid.value,
      peerName: currentPeerName.value,
      direction: callDirection.value,
      state: callState.value,
      duration:
        callState.value === 'connected' && callStartTime.value
          ? Math.floor((Date.now() - callStartTime.value) / 1000)
          : 0,
    }
    resetCallState()
    if (tone) playDisconnectSound()
    const status = outcome || outcomeFor(reason, call)
    if (status) logCall(call, status)
    if (publish) publishPresence()
    return true
  }

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────

  function resetCallState() {
    // Catch-all tone stop: every teardown path lands here (incl. caller hangup
    // while the outbound ringback is still playing), and stopRingtone is
    // idempotent — a tone must never outlive its call.
    stopRingtone()
    clearAnswerTimer()
    callState.value = 'idle'
    answering.value = false
    callDirection.value = null
    currentPeerUid.value = null
    currentPeerName.value = null
    currentCallId.value = null
    callStartTime.value = null
    isMuted.value = false
    remoteStream.value = null
  }

  // Quick call from history
  async function callFromHistory(historyEntry) {
    const isCallerMe = historyEntry.callerUid === myUid.value
    const peerUid = isCallerMe ? historyEntry.receiverUid : historyEntry.callerUid
    const peerName = isCallerMe ? historyEntry.receiverName : historyEntry.callerName

    return await startCall(peerUid, peerName)
  }

  // ─────────────────────────────────────────────────────────────
  // Return
  // ─────────────────────────────────────────────────────────────

  return {
    // State
    myUid,
    myName,
    myPhotoURL,
    myStatus,
    onlineUsers,
    remoteStream,
    callState,
    answering,
    currentPeerUid,
    currentPeerName,
    callStartTime,
    isMuted,
    callHistory,

    // Computed
    availableUsers,
    busyUsers,
    allOnlineUsers,
    isInCall,
    recentCalls,

    // Actions
    initialize,
    cleanup,
    setMyStatus,
    startCall,
    setTeamVoiceAdapter,
    setAudioOwnership,
    acceptCall,
    declineCall,
    hangup,
    toggleMute,
    callFromHistory,
    requestNotificationPermission,
  }
})
